/**
 * Zones (แผนที่): the hub town of ลานศิลาเก้ากระบี่, the Snow Peak, and one zone per school — its
 * compound in the middle, hunting grounds round the outside. Zones sit on a world grid; each one
 * has a warp circle at the middle of an edge for every neighbour on that side, so going far means
 * passing through the zones between. Each zone is a map of its own; travelling reloads the world.
 * Add a zone here (with a free grid place next to another) and its map, monsters and warps follow.
 */
import type { Biome, RegionKey } from '../map';
import { YANYU2_ZONE } from './layouts/yanyu2';
import { ZHUMING_ZONE } from './layouts/zhuming';
import { TOWN_B_ZONE } from './layouts/townB';
import { TOWN_C_ZONE } from './layouts/townC';
import { TOWN_D_ZONE } from './layouts/townD';
import { SECT_ZONES } from './layouts/sects';
import { CITY_ZONES } from './layouts/cities';

export type ZoneId =
  | 'yanyu'
  | 'plains'
  | 'snow'
  | 'mist-peak'
  | 'bell-stone'
  | 'lantern-road'
  | 'dim-moon'
  | 'pine-ink'
  | 'cinnabar'
  /** The render-v2 demo plaza (`?r=2`): the gpudemo's plaza as a zone, off the world grid's links. */
  | 'demoPlaza'
  /** The new Yanyu greybox (plan B, data/layouts/yanyu2.ts): the ko view's `?map=yanyu2`. */
  | 'yanyu2'
  /** จู๋หมิง, the 1 km starter region (data/layouts/zhuming.ts): `?map=zhuming`. */
  | 'zhuming'
  /** H7 greybox start towns of origins B-D (data/layouts/townB.ts ...). */
  | 'town-b'
  | 'town-c'
  | 'town-d'
  | `sect-${'bamboo-tide' | 'mist-peak' | 'cinnabar' | 'pine-ink' | 'bell-stone' | 'lantern-road' | 'dim-moon' | 'snow-pine'}` // H12
  | `city-${'trade' | 'sea' | 'desert' | 'snow'}`; // H13

export interface ZoneSpawn {
  readonly monster: string;
  readonly region: RegionKey;
  readonly n: number;
}

export interface ZoneDef {
  readonly id: ZoneId;
  readonly name: string;
  /**
   * hub: the town with every warp; school: a school's compound; wild: open country; village: a
   * hand-laid village (the starting one: a harbour, an inn, rice fields under the mountains).
   */
  readonly kind: 'hub' | 'school' | 'wild' | 'village';
  /** Place on the world grid (x east, y south). Zones side by side are linked by warp circles. */
  readonly at: readonly [number, number];
  /** Ground look and size of the map. */
  readonly terrain: Biome;
  readonly size: number;
  readonly seed: number;
  /**
   * An authored layout (data/layouts/<id>.ts, built by layout.ts): the map is laid out by hand and
   * never depends on the world seed. Without one the map is generated from the seed.
   */
  readonly layout?: string;
  /**
   * The open-world region this zone becomes (data/regions/<id>.ts, docs/archive/OPEN_WORLD.md): its layout
   * is stamped as the region's core. Used once region maps are on (region/zone.ts).
   */
  readonly region?: string;
  /** A sketch region off the world grid (not one of the nine lands; no station or sword yet): rules over the lands skip it. */
  readonly offGrid?: boolean;
  /** Colour cast over the ground (multipliers), so each school's land looks its own. */
  readonly tint?: readonly [number, number, number];
  /** The school whose compound this is. */
  readonly school?: string;
  /**
   * Background music, presentational only: the id of the track the client plays here
   * (apps/client/src/music/tracks), the one at night (default 'night'), and the one out in the
   * wilds away from the town or compound (default: `music`).
   */
  readonly music: string;
  readonly nightMusic?: string;
  readonly wildMusic?: string;
  /** Levels of the monsters, for the warp labels. */
  readonly levels: string;
  readonly spawns: readonly ZoneSpawn[];
  /**
   * The player's walking speed here (m/s), in place of their stats' (gear and neigong do not change it): a zone
   * whose figures walk at one gait (the render-v2 demo plaza, where the player walks beside its crowd) sets it.
   * Left out, the player's own speed.
   */
  readonly walkSpeed?: number;
}

export const HUB: ZoneId = 'plains';
/** Where a new character wakes up. */
export const START: ZoneId = 'yanyu2';

/**
 * The old village (layout ข, the bay and shrine hill; docs/archive/YANYU.md Part B), kept but off the world grid: the new Yanyu
 * (`yanyu2`) took its place at (0, -1). In `ZONES` only (its map is made like any other), not in `ZONE_LIST`.
 */
const YANYU_OLD: ZoneDef = {
    id: 'yanyu',
    at: [60, 61],
    name: 'หมู่บ้านหยานหยู',
    kind: 'village',
    terrain: 'plains',
    // The region's side. A zone with a layout takes its map's size from the layout, so while
    // region maps are off (`setRegionMaps`) the village is still its own 160 m map.
    size: 3456,
    seed: 307,
    // Layout ข, the bay and shrine hill (docs/archive/YANYU.md).
    layout: 'yanyu',
    // The Yanyu region (docs/archive/OPEN_WORLD.md option B; docs/archive/HUB.md option ก), its bounding square 3,456 m, with the village at its core.
    region: 'yanyu',
    tint: [1.02, 1.03, 0.98],
    music: 'yanyu',
    wildMusic: 'wilds',
    levels: 'ระดับ 1–2',
    // A fishing village: wolves in the pine wood and down the eastern hills, bandits lurk past the
    // fields, and sparring partners wait in the training yard (its `west` region).
    spawns: [
      { monster: 'wolf', region: 'wolves', n: 28 },
      { monster: 'wolf', region: 'east', n: 14 },
      { monster: 'bandit', region: 'bandits', n: 28 },
      { monster: 'sparrer', region: 'west', n: 4 },
    ],
  };

const list: ZoneDef[] = [
  YANYU2_ZONE,
  {
    id: 'plains',
    at: [0, 0],
    name: 'ลานศิลาเก้ากระบี่',
    kind: 'hub',
    terrain: 'plains',
    // 72 m across, the map of the 2026-10-04 client (commit 9fbc89f0: generateMap(9, 72, 72)) — user,
    // 2026-10-10: "take the map and scenery from that commit" (was 288, and 100 for a few hours on 10-09).
    size: 72,
    seed: 0,
    music: 'hub',
    wildMusic: 'wilds',
    levels: 'ระดับ 2–5',
    // The first hunts stay by the town: wolves to the north-east, bandits to the south-west.
    // 18 wolves and 18 bandits, as on the 9fbc89f0 map (it had no knifers).
    spawns: [
      { monster: 'wolf', region: 'wolves', n: 18 },
      { monster: 'bandit', region: 'bandits', n: 18 },
    ],
  },
  {
    id: 'mist-peak',
    at: [-1, -1],
    name: 'ภูเขายอดหมอก',
    kind: 'school',
    school: 'mist-peak',
    terrain: 'plains',
    size: 240,
    seed: 211,
    tint: [0.9, 1.02, 1.06],
    music: 'mist-peak',
    levels: 'ระดับ 7–8',
    // Taoists against the hopping dead: the graves below the mountain.
    spawns: [
      { monster: 'jiangshi', region: 'graves', n: 160 },
      { monster: 'jiangshi', region: 'wolves', n: 128 },
      { monster: 'wolf', region: 'bandits', n: 128 },
      { monster: 'spider', region: 'east', n: 112 },
      { monster: 'python', region: 'west', n: 96 },
    ],
  },
  {
    id: 'bell-stone',
    at: [-1, 0],
    name: 'วัดหินระฆังแห่งเขาพันบันได',
    kind: 'school',
    school: 'bell-stone',
    terrain: 'plains',
    size: 240,
    seed: 223,
    tint: [1.08, 1.0, 0.84],
    music: 'bell-stone',
    levels: 'ระดับ 7–8',
    // The renegade monks of a ruined temple, and the black bears of the Song mountains.
    spawns: [
      { monster: 'monk', region: 'west', n: 160 },
      { monster: 'monk', region: 'bandits', n: 96 },
      { monster: 'bear', region: 'east', n: 128 },
      { monster: 'bear', region: 'wolves', n: 96 },
    ],
  },
  {
    id: 'lantern-road',
    at: [-1, -2],
    name: 'ค่ายทางโคมเหล็ก',
    kind: 'school',
    school: 'lantern-road',
    terrain: 'plains',
    size: 240,
    seed: 227,
    tint: [0.94, 0.95, 1.02],
    music: 'lantern-road',
    levels: 'ระดับ 5–6',
    // The guards hunt the assassins in black and the knife-throwers who hide them.
    spawns: [
      { monster: 'assassin', region: 'graves', n: 128 },
      { monster: 'assassin', region: 'wolves', n: 96 },
      { monster: 'knifer', region: 'bandits', n: 160 },
      { monster: 'leopard', region: 'east', n: 112 },
    ],
  },
  {
    id: 'dim-moon',
    at: [1, 0],
    name: 'หุบเขาจันทร์มัว',
    kind: 'school',
    school: 'dim-moon',
    terrain: 'plains',
    size: 240,
    seed: 229,
    tint: [1.1, 0.94, 1.02],
    music: 'dim-moon',
    nightMusic: 'dim-moon',
    levels: 'ระดับ 2–6',
    spawns: [
      { monster: 'wolf', region: 'wolves', n: 160 },
      { monster: 'bandit', region: 'bandits', n: 128 },
      { monster: 'assassin', region: 'graves', n: 96 },
      { monster: 'boar', region: 'east', n: 96 },
      { monster: 'viper', region: 'west', n: 96 },
    ],
  },
  {
    id: 'pine-ink',
    at: [0, 1],
    name: 'หอหมึกสน',
    kind: 'school',
    school: 'pine-ink',
    terrain: 'plains',
    size: 240,
    seed: 233,
    tint: [1.02, 1.02, 0.95],
    music: 'pine-ink',
    levels: 'ระดับ 4–7',
    spawns: [
      { monster: 'bandit', region: 'bandits', n: 160 },
      { monster: 'knifer', region: 'west', n: 96 },
      { monster: 'jiangshi', region: 'graves', n: 128 },
      { monster: 'viper', region: 'wolves', n: 96 },
      { monster: 'scorpion', region: 'east', n: 96 },
    ],
  },
  {
    id: 'cinnabar',
    at: [1, 1],
    name: 'ผาดาบชาด',
    kind: 'school',
    school: 'cinnabar',
    terrain: 'plains',
    size: 240,
    seed: 239,
    tint: [1.08, 0.84, 0.82],
    music: 'cinnabar',
    nightMusic: 'cinnabar',
    levels: 'ระดับ 6–8',
    spawns: [
      { monster: 'assassin', region: 'west', n: 128 },
      { monster: 'bear', region: 'wolves', n: 128 },
      { monster: 'jiangshi', region: 'graves', n: 128 },
      { monster: 'bloodbat', region: 'east', n: 112 },
      { monster: 'centipede', region: 'bandits', n: 112 },
    ],
  },
  {
    id: 'snow',
    at: [-1, -3],
    name: 'ยอดเขาหิมะ',
    kind: 'wild',
    terrain: 'snow',
    size: 256,
    seed: 101,
    music: 'snow',
    nightMusic: 'snow',
    levels: 'ระดับ 9–11',
    spawns: [
      { monster: 'snowwolf', region: 'wolves', n: 192 },
      { monster: 'snowwolf', region: 'east', n: 96 },
      { monster: 'frostman', region: 'bandits', n: 160 },
      { monster: 'frostman', region: 'west', n: 96 },
      { monster: 'foxspirit', region: 'graves', n: 96 },
      { monster: 'whitetiger', region: 'east', n: 40 },
    ],
  },
];

/**
 * The render-v2 demo plaza (`?r=2`, data/layouts/demoPlaza.ts): the gpudemo's plaza as a zone. It is in
 * `ZONES` (its map is made like any other), not in `ZONE_LIST` (no warps, stations, spots or swords): the v2
 * client starts the World there (`progress.location`). Wild, so no carriage station is expected.
 */
const DEMO_PLAZA: ZoneDef = {
  id: 'demoPlaza',
  at: [40, 40],
  name: 'ลานสาธิต',
  kind: 'wild',
  terrain: 'plains',
  size: 80,
  seed: 80,
  layout: 'demoPlaza',
  music: 'yanyu',
  levels: 'ระดับ 2',
  // One wolf camp in the plaza's open south-east corner (the layout's `wolves` region). The plaza stays safe
  // (no PK); the monsters are fought as anywhere.
  spawns: [{ monster: 'wolf', region: 'wolves', n: 5 }],
  // The crowd walkers' pace (tools/demo25d/shared.ts: `m.walkSpeed * dt` at rate 1): the walk gait of Model 0 (the
  // man's, render3d motions `gaitSpeed('walk')`, 1.372 m/s, as the male NPCs'); the ko view plays the walk at rate 1
  // at it.
  walkSpeed: 1.372,
};

export const ZONES: Readonly<Record<ZoneId, ZoneDef>> = Object.fromEntries(
  [...list, YANYU_OLD, DEMO_PLAZA, ZHUMING_ZONE, TOWN_B_ZONE, TOWN_C_ZONE, TOWN_D_ZONE, ...SECT_ZONES, ...CITY_ZONES].map((z) => [z.id, z]),
) as Record<ZoneId, ZoneDef>;
export const ZONE_LIST: readonly ZoneDef[] = list;
export const isZoneId = (v: unknown): v is ZoneId => typeof v === 'string' && v in ZONES;

export type Side = 'n' | 'e' | 's' | 'w';
const STEP: Readonly<Record<Side, readonly [number, number]>> = {
  n: [0, -1],
  e: [1, 0],
  s: [0, 1],
  w: [-1, 0],
};

/** A zone's warp circles: the neighbour on each side of it on the world grid. */
export function linksOf(id: ZoneId): { side: Side; to: ZoneId }[] {
  const [x, y] = ZONES[id].at;
  return (Object.keys(STEP) as Side[]).flatMap((side) => {
    const [dx, dy] = STEP[side];
    const n = list.find((z) => z.at[0] === x + dx && z.at[1] === y + dy);
    return n ? [{ side, to: n.id }] : [];
  });
}

/** The fewest warps from one zone to another (breadth-first over the grid links), or -1. */
export function warpDistance(from: ZoneId, to: ZoneId): number {
  const seen = new Map<ZoneId, number>([[from, 0]]);
  const queue: ZoneId[] = [from];
  while (queue.length) {
    const z = queue.shift()!;
    if (z === to) return seen.get(z)!;
    for (const l of linksOf(z))
      if (!seen.has(l.to)) {
        seen.set(l.to, seen.get(z)! + 1);
        queue.push(l.to);
      }
  }
  return -1;
}

/** The zone of a school. */
export const schoolZone = (school: string): ZoneDef | undefined => list.find((z) => z.school === school);

/**
 * PLAINS256 (lane C0-P1, docs/design/C0_TOWN_LAYOUT.md): the plains hub as the approved 256 × 256 m map
 * (data/layouts/plains.ts, laid by plainsMap.ts) in place of the 72 m map above. On since 2026-10-10 (lane C0-P1, perf
 * gate passed). Rollback: `false`. The client may set it for a session before the World is made (`setPlains256`);
 * the 72 m suites in the tests pin it off the same way.
 */
export const PLAINS256 = true;
let plains256Override: boolean | null = null;
/** Whether the plains are the 256 m map. */
export const plains256 = (): boolean => plains256Override ?? PLAINS256;
/** Sets the switch for this session (`null`: back to `PLAINS256`). Call before any World or map is made. */
export const setPlains256 = (on: boolean | null): void => {
  plains256Override = on;
};
